import type { SpaceDetailDto } from './spaces';
import { getJson, patchJson, postJson } from './client';

// --- Types (partner RoomController, owner-scoped) ---

export type RoomImageTicket = {
  mediaId: string;
  ownershipTicket: string;
  imageUrl: string;
};

export type RoomFacilityRequest = {
  facilityKey: string;
  label: string;
};

export type RoomOptionRequest = {
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  mediaId?: string;
  ownershipTicket?: string;
};

export type RoomReservationFieldRequest = {
  inputType: string;
  title: string;
  required: boolean;
  inputFrequency?: string;
  sortOrder: number;
  options?: string[];
};

export type RoomTimeSlotPriceRequest = {
  days: string[];
  timeRange: string;
  price: number;
};

export type RoomUpsertRequest = {
  slug?: string;
  name: string;
  description?: string;
  categoryLabel?: string;
  capacityMin?: number;
  capacityMax?: number;
  basePrice?: number;
  priceUnit?: string;
  imageUrls?: string[];
  imageTickets?: RoomImageTicket[];
  facilities?: RoomFacilityRequest[];
  options?: RoomOptionRequest[];
  reservationFields?: RoomReservationFieldRequest[];
  timeSlotPrices?: RoomTimeSlotPriceRequest[];
  bookingMethod?: string;
  pricingMode?: string;
  fixedPrice?: number;
  scheduleStart?: string;
  scheduleEnd?: string;
  alwaysOpen?: boolean;
  sameDayLimit?: boolean;
  holidayNote?: string;
  operatingDays?: unknown;
  closedBlocks?: unknown;
  selectedCouponIds?: string[];
  isListed?: boolean;
};

export type RoomCommandImageItem = {
  imageUrl: string | null;
  mediaId: string | null;
  sortOrder: number;
};

export type RoomCommandResponse = {
  roomId: string;
  images: RoomCommandImageItem[];
};

export type OwnerRoomSummary = {
  roomId: string;
  slug: string | null;
  name: string;
  status: string;
  imageUrl: string | null;
  mediaId: string | null;
  location: string | null;
  basePrice: number;
  priceUnit: string | null;
  rating: string | null;
  reviewCount: number;
  tags: string[];
};

/**
 * Owner room detail = public RoomDetailResponse plus editing fields the
 * owner console needs (bookingMethod, pricingMode, schedule, ...).
 */
export type OwnerRoomDetail = SpaceDetailDto & {
  capacityMin?: number | null;
  capacityMax?: number | null;
  timeSlotPrices?: RoomTimeSlotPriceRequest[] | null;
  bookingMethod?: string | null;
  pricingMode?: string | null;
  fixedPrice?: number | null;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
  alwaysOpen?: boolean | null;
  sameDayLimit?: boolean | null;
  holidayNote?: string | null;
  operatingDays?: unknown;
  closedBlocks?: unknown;
  selectedCouponIds?: string[] | null;
  isListed?: boolean | null;
};

export type RoomVisibility = 'LISTED' | 'HIDDEN';

// --- Functions ---

export function createRoom(studioId: string, req: RoomUpsertRequest) {
  return postJson<RoomCommandResponse>(
    `/api/v1/studios/${encodeURIComponent(studioId)}/rooms`,
    req,
  );
}

export function getStudioRooms(studioId: string) {
  return getJson<OwnerRoomSummary[]>(`/api/v1/studios/${encodeURIComponent(studioId)}/rooms`);
}

export function reorderStudioRooms(studioId: string, roomIds: string[]) {
  return patchJson<OwnerRoomSummary[]>(
    `/api/v1/studios/${encodeURIComponent(studioId)}/rooms/order`,
    { roomIds },
  );
}

export function updateRoom(roomId: string, req: RoomUpsertRequest) {
  return patchJson<RoomCommandResponse>(`/api/v1/rooms/${encodeURIComponent(roomId)}`, req);
}

export function getOwnerRoomDetail(roomId: string) {
  return getJson<OwnerRoomDetail>(`/api/v1/rooms/${encodeURIComponent(roomId)}`);
}

export function updateRoomVisibility(roomId: string, visibility: RoomVisibility) {
  return patchJson<RoomCommandResponse>(`/api/v1/rooms/${encodeURIComponent(roomId)}/visibility`, {
    visibility,
  });
}
